import { OpenInNew } from '@mui/icons-material'
import { Box, Chip, Link, Typography } from '@mui/material'
import moment from 'moment'
import { FC, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { CustomModal } from '../../../components/common/CustomModal'
import { CASE_DETAIL_ROUTE, NOT_APPLICABLE } from '../../../constants'
import {
  COURT_DATE_TIMING_STYLES,
  CourtDateEvent,
  getCourtDateTiming,
  getMatterNumber,
  getMatterStatusStyle
} from './courtDatesUtils'

interface CourtDateDetailModalProps {
  event: CourtDateEvent | null
  onClose: () => void
}

const DetailRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Box display="flex" gap={2} py={1} borderBottom="1px solid" borderColor="lightGray">
    <Typography color="lightGrayOne" fontSize="14px" minWidth="110px">
      {label}
    </Typography>
    <Typography component="div" fontSize="14px" sx={{ wordBreak: 'break-word' }}>
      {children || NOT_APPLICABLE}
    </Typography>
  </Box>
)

export const CourtDateDetailModal: FC<CourtDateDetailModalProps> = ({ event, onClose }) => {
  if (!event) return null

  const { start, end, title, courtDate } = event
  const { caseId, caseTitle, caseStatus, clientName, courtName, responsibleAttorneyName } = courtDate
  const matterNumber = getMatterNumber(courtDate)
  const status = getMatterStatusStyle(caseStatus)
  const timing = COURT_DATE_TIMING_STYLES[getCourtDateTiming(event)]

  return (
    <CustomModal isOpen={!!event} handleClose={onClose} title={title} maxWidth="sm">
      <DetailRow label="Date">
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          {moment(start).format('dddd, MMM D, YYYY')}
          <Chip
            size="small"
            variant="outlined"
            label={timing.label}
            sx={{ borderColor: timing.color, color: timing.color, fontWeight: 500 }}
          />
        </Box>
      </DetailRow>
      <DetailRow label="Time">{`${moment(start).format('h:mm A')} – ${moment(end).format('h:mm A')}`}</DetailRow>
      <DetailRow label="Matter">
        {matterNumber ? `${matterNumber}${caseTitle ? ` · ${caseTitle}` : ''}` : caseTitle}
      </DetailRow>
      <DetailRow label="Matter status">
        <Chip
          size="small"
          variant="outlined"
          icon={<status.Icon />}
          label={status.label}
          sx={{ fontWeight: 500, '& .MuiChip-icon': { fontSize: '16px' } }}
        />
      </DetailRow>
      <DetailRow label="Client">{clientName}</DetailRow>
      <DetailRow label="Court">{courtName}</DetailRow>
      <DetailRow label="Resp 1 Attorney">{responsibleAttorneyName}</DetailRow>

      <Box display="flex" justifyContent="flex-end" mt={3}>
        {/* A new tab, so the calendar (view, date, attorney filter) stays where it was */}
        <Link
          component={RouterLink}
          to={`${CASE_DETAIL_ROUTE}/${caseId}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="primary"
          fontSize="14px"
          fontWeight={500}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          Open Matter
          <OpenInNew sx={{ fontSize: '16px' }} />
        </Link>
      </Box>
    </CustomModal>
  )
}
